import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Buildings, Files, Users, Handshake, ChatCircleText, Archive, TrendUp } from "@phosphor-icons/react";
import { formatDate, timeAgo } from "@/lib/format";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-lg border border-border bg-card p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="mt-1 num-display font-display text-3xl font-semibold">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </div>
      <div className="h-9 w-9 rounded-md bg-secondary grid place-items-center">
        <Icon size={18} className="text-emerald-600" />
      </div>
    </div>
  </div>
);

export default function AdminHome() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);
  if (!stats) return <div className="p-8 text-muted-foreground">Loading…</div>;
  const t = stats.totals;
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Admin</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Portal overview</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/companies" className="text-sm text-emerald-700 dark:text-emerald-400 hover:underline">Manage companies →</Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat icon={Buildings} label="Live Companies" value={t.live_companies} sub={`${t.companies} total`} testid="stat-live-companies" />
        <Stat icon={Archive} label="Archived" value={t.archived_companies} testid="stat-archived" />
        <Stat icon={Files} label="Documents" value={t.documents} testid="stat-documents" />
        <Stat icon={Users} label="Investors" value={t.investors} sub={t.pending_investors ? `${t.pending_investors} pending approval` : "All approved"} testid="stat-investors" />
        <Stat icon={Handshake} label="Meeting Requests" value={t.meetings} testid="stat-meetings" />
        <Stat icon={ChatCircleText} label="Enquiries" value={t.enquiries} testid="stat-enquiries" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">Industry distribution</div>
            <TrendUp size={14} className="text-muted-foreground" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.industry_distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="industry" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                <Bar dataKey="count" fill="hsl(161 94% 30%)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-5">
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-3">Most viewed companies</div>
          <div className="space-y-2">
            {stats.most_viewed_companies.map((c, i) => (
              <Link key={c.id} to={`/companies/${c.id}`} className="flex items-center gap-3 p-2 rounded hover:bg-secondary/60 transition-colors">
                <div className="w-5 num-display text-sm text-muted-foreground">{i + 1}</div>
                <div className="h-8 w-8 rounded bg-secondary overflow-hidden">
                  {c.logo_url && <img src={c.logo_url} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.industry}</div>
                </div>
                <div className="num-display text-sm font-medium">{c.view_count}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-border bg-card p-5">
        <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-3">Recent activity</div>
        <div className="space-y-1.5">
          {stats.recent_activity.map((a) => (
            <div key={a.id} className="flex items-center gap-3 py-1.5 text-sm">
              <div className="text-xs text-muted-foreground w-20 shrink-0">{timeAgo(a.created_at)}</div>
              <div className="text-xs uppercase tracking-wider text-emerald-700 dark:text-emerald-400 w-32 shrink-0">{a.action.replace(/_/g, " ")}</div>
              <div className="flex-1 truncate text-muted-foreground">{a.user_email} {a.metadata?.name ? `· ${a.metadata.name}` : ""}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
